import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faStopwatch,
    faHourglassHalf,
    faWalking,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

const TimeAnalytics = ({ status_time, WALK_STATUS }) => {
    if (!status_time || Object.keys(status_time).length === 0) {
        return null;
    }

    const calculateTimeAnalytics = () => {
        const analytics = {
            responseTime: null,
            waitingTime: null,
            walkDuration: null,
            totalDuration: null,
            pickupTime: null,
            travelTime: null
        };

        try {
            // Helper function to calculate time difference in minutes
            const calculateTimeDiff = (startTime, endTime) => {
                if (!startTime || !endTime) return null;
                const start = new Date(startTime);
                const end = new Date(endTime);
                const diffMs = end - start;
                const diffMinutes = diffMs / (1000 * 60);
                // Return at least 1 minute for very small differences
                return Math.max(1, Math.round(diffMinutes));
            };

            // Response Time: From request to walker assignment
            if (status_time[WALK_STATUS.REQUESTED] && status_time[WALK_STATUS.WALKER_ASSIGNED]) {
                analytics.responseTime = calculateTimeDiff(
                    status_time[WALK_STATUS.REQUESTED],
                    status_time[WALK_STATUS.WALKER_ASSIGNED]
                );
            }

            // Waiting Time: From assignment to pickup
            if (status_time[WALK_STATUS.WALKER_ASSIGNED] && status_time[WALK_STATUS.PICKED_UP]) {
                analytics.waitingTime = calculateTimeDiff(
                    status_time[WALK_STATUS.WALKER_ASSIGNED],
                    status_time[WALK_STATUS.PICKED_UP]
                );
            }

            // Walk Duration: From in_progress to completed
            if (status_time[WALK_STATUS.IN_PROGRESS] && status_time[WALK_STATUS.COMPLETED]) {
                analytics.walkDuration = calculateTimeDiff(
                    status_time[WALK_STATUS.IN_PROGRESS],
                    status_time[WALK_STATUS.COMPLETED]
                );
            }

            // Travel Time: From heading_to_pickup to picked_up
            if (status_time[WALK_STATUS.HEADING_TO_PICKUP] && status_time[WALK_STATUS.PICKED_UP]) {
                analytics.travelTime = calculateTimeDiff(
                    status_time[WALK_STATUS.HEADING_TO_PICKUP],
                    status_time[WALK_STATUS.PICKED_UP]
                );
            }

            // Total Duration: From request to completion
            if (status_time[WALK_STATUS.REQUESTED] && status_time[WALK_STATUS.COMPLETED]) {
                analytics.totalDuration = calculateTimeDiff(
                    status_time[WALK_STATUS.REQUESTED],
                    status_time[WALK_STATUS.COMPLETED]
                );
            }

            // Pickup Time: From picked_up to in_progress
            if (status_time[WALK_STATUS.PICKED_UP] && status_time[WALK_STATUS.IN_PROGRESS]) {
                analytics.pickupTime = calculateTimeDiff(
                    status_time[WALK_STATUS.PICKED_UP],
                    status_time[WALK_STATUS.IN_PROGRESS]
                );
            }

            // Debug logging
            console.log('Walk Analytics Debug:', {
                status_time,
                calculatedAnalytics: analytics
            });

        } catch (error) {
            console.error('Error calculating time analytics:', error);
        }

        return analytics;
    };

    const analytics = calculateTimeAnalytics();

    const formatTime = (minutes) => {
        if (minutes === null || minutes === undefined) return 'N/A';
        if (minutes === 0) return 'N/A';
        if (minutes < 1) return '< 1 min';
        if (minutes < 60) return `${Math.round(minutes)} min`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    const getTimeQuality = (time, threshold) => {
        if (time === null || time === undefined) return 'neutral';
        return time <= threshold ? 'good' : 'warning';
    };

    return (
        <div className="mt-4 sm:mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-lg border border-blue-200">
            <h4 className="text-gray-700 font-semibold mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                <FontAwesomeIcon icon={faStopwatch} className="mr-2 text-blue-600" />
                Time Analytics
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {/* Response Time */}
                <div className={`p-3 rounded-lg border ${getTimeQuality(analytics.responseTime, 5) === 'good'
                    ? 'bg-green-50 border-green-200'
                    : getTimeQuality(analytics.responseTime, 5) === 'warning'
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faClock} className="text-blue-600" />
                        <span className="font-semibold text-sm">Response Time</span>
                    </div>
                    <div className="text-lg font-bold text-gray-800 mt-1">
                        {formatTime(analytics.responseTime)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                        Request to assignment
                    </div>
                </div>

                {/* Waiting Time */}
                <div className={`p-3 rounded-lg border ${getTimeQuality(analytics.waitingTime, 20) === 'good'
                    ? 'bg-green-50 border-green-200'
                    : getTimeQuality(analytics.waitingTime, 20) === 'warning'
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faHourglassHalf} className="text-orange-600" />
                        <span className="font-semibold text-sm">Waiting Time</span>
                    </div>
                    <div className="text-lg font-bold text-gray-800 mt-1">
                        {formatTime(analytics.waitingTime)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                        Assignment to pickup
                    </div>
                </div>

                {/* Walk Duration */}
                <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
                    <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faWalking} className="text-green-600" />
                        <span className="font-semibold text-sm">Walk Duration</span>
                    </div>
                    <div className="text-lg font-bold text-gray-800 mt-1">
                        {formatTime(analytics.walkDuration)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                        Actual walk time
                    </div>
                </div>

                {/* Travel Time */}
                {analytics.travelTime && (
                    <div className="p-3 rounded-lg border bg-purple-50 border-purple-200">
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faClock} className="text-purple-600" />
                            <span className="font-semibold text-sm">Travel Time</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.travelTime)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            To pickup location
                        </div>
                    </div>
                )}

                {/* Pickup Time */}
                {analytics.pickupTime && (
                    <div className="p-3 rounded-lg border bg-indigo-50 border-indigo-200">
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faClock} className="text-indigo-600" />
                            <span className="font-semibold text-sm">Pickup Time</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.pickupTime)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Arrival to start
                        </div>
                    </div>
                )}

                {/* Total Duration */}
                <div className="p-3 rounded-lg border bg-gray-50 border-gray-200">
                    <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faStopwatch} className="text-gray-600" />
                        <span className="font-semibold text-sm">Total Duration</span>
                    </div>
                    <div className="text-lg font-bold text-gray-800 mt-1">
                        {formatTime(analytics.totalDuration)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                        Request to completion
                    </div>
                </div>
            </div>

            {/* Performance Indicators */}
            <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center space-x-2 mb-2">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600" />
                    <span className="font-semibold text-sm text-gray-700">Performance Indicators</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className={`px-2 py-1 rounded ${getTimeQuality(analytics.responseTime, 5) === 'good' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                        Response: {getTimeQuality(analytics.responseTime, 5) === 'good' ? '✓ Good' : '⚠ Slow'}
                    </div>
                    <div className={`px-2 py-1 rounded ${getTimeQuality(analytics.waitingTime, 20) === 'good' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                        Waiting: {getTimeQuality(analytics.waitingTime, 20) === 'good' ? '✓ Good' : '⚠ Long'}
                    </div>
                    <div className="px-2 py-1 rounded bg-blue-100 text-blue-800">
                        Walk: {analytics.walkDuration ? '✓ Complete' : '⏳ In Progress'}
                    </div>
                    <div className="px-2 py-1 rounded bg-gray-100 text-gray-600">
                        Total: {analytics.totalDuration ? '✓ Complete' : '⏳ Ongoing'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimeAnalytics; 